import '@dnb/eufemia/style';

import { GlobalError, GlobalStatus, Icon, Logo } from '@dnb/eufemia';
import {
  chip_medium as ChipIcon,
  cloud_transfer_medium as CloudTransferIcon,
  credit_note_medium as CreditNoteIcon,
  email_medium as EmailIcon,
  exclamation_circled_medium as ExclamationIcon,
  globe_medium as GlobeIcon,
  megaphone_medium as MegaphoneIcon,
  office_buildings_medium as OfficBuildingIcon,
  padlock_medium as PadlockIcon,
  people_2_medium as People2Icon,
  person_medium as PersonIcon,
  refresh_medium as RefreshIcon,
  shield_lock as ShieldLockIcon,
  split_medium as SplitIcon,
  support_medium as SupportIcon,
  tag_medium as TagIcon,
  widget_android_medium as WidgetAndroidIcon,
} from '@dnb/eufemia/icons';
import ErrorContext from '@portals/shared-frontend/hooks/ErrorContext';
import classNames from 'classnames';
import React, { ReactNode, useContext, useEffect, useRef } from 'react';
import { NavLink, type NavLinkProps, Outlet, useLocation } from 'react-router-dom';

import useFeatureFlags from './hooks/useFeatureFlags';

import style from './App.module.css';

interface IconNavLinkProps extends NavLinkProps {
  icon: React.FC;
}

const IconNavLink = ({ icon, ...props }: IconNavLinkProps) => {
  return (
    <NavLink
      {...props}
      className={({ isActive }) =>
        classNames({
          [style['NavLink']!]: true,
          [style['ActiveNavLink']!]: isActive,
          ['dnb-tab-focus']: true,
        })
      }
    >
      <Icon icon={icon} right="x-small" size="medium" />
      {props.children as ReactNode}
    </NavLink>
  );
};

function App() {
  const { globalError, setGlobalError } = useContext(ErrorContext);
  const location = useLocation();
  const initialLocationKeyRef = useRef(location.key);
  const { featureFlags } = useFeatureFlags();

  // Reset error on navigation, also works when navigating to the same page
  useEffect(() => {
    if (location.key !== initialLocationKeyRef.current) {
      setGlobalError(null);
    }
  }, [location.key, setGlobalError]);

  return (
    <div className={style['App']}>
      <header
        className={classNames(style['Bar'], {
          'bg-accent-yellow-30': featureFlags.ENABLE_ENV_COLOR_INDICATOR,
        })}
      >
        <NavLink to="/">
          <Logo size={48} />
        </NavLink>
      </header>
      <div className={style['Content']}>
        <nav className={style['Navigation']}>
          <ul>
            <li>
              <IconNavLink icon={CloudTransferIcon} to="/apis">
                APIs
              </IconNavLink>
              <IconNavLink icon={WidgetAndroidIcon} to="/projects">
                Projects
              </IconNavLink>
              <IconNavLink icon={WidgetAndroidIcon} to="/apps">
                Apps
              </IconNavLink>
              <IconNavLink icon={ExclamationIcon} to="/api-incidents">
                Status
              </IconNavLink>
              <IconNavLink icon={EmailIcon} to="/subscribers">
                Subscribers
              </IconNavLink>
              <IconNavLink icon={MegaphoneIcon} to="/news">
                News
              </IconNavLink>
              <IconNavLink icon={SupportIcon} to="/feedback">
                Feedback
              </IconNavLink>
              <IconNavLink icon={PersonIcon} to="/users">
                Users
              </IconNavLink>
              <IconNavLink icon={People2Icon} to="/teams">
                Teams
              </IconNavLink>
              <IconNavLink icon={OfficBuildingIcon} to="/organizations">
                Organizations
              </IconNavLink>
              <IconNavLink icon={PadlockIcon} to="/api-grants">
                Grants
              </IconNavLink>
              <IconNavLink icon={SplitIcon} to="/feature-flags">
                Feature flags
              </IconNavLink>
              <IconNavLink icon={TagIcon} to="/tags">
                Tags
              </IconNavLink>
              <IconNavLink icon={PadlockIcon} to="/templates">
                Templates
              </IconNavLink>
              <IconNavLink icon={CreditNoteIcon} to="/content">
                Content
              </IconNavLink>
              <IconNavLink icon={GlobeIcon} to="/scopes">
                Scopes
              </IconNavLink>
              <IconNavLink icon={ChipIcon} to="/clients">
                OAuth Clients
              </IconNavLink>
              <IconNavLink icon={PadlockIcon} to="/entitlements">
                Entitlements
              </IconNavLink>
              <IconNavLink icon={ShieldLockIcon} to="/sn-service-domains">
                Service Domains
              </IconNavLink>
              <IconNavLink icon={RefreshIcon} to="/embeddings">
                Embeddings
              </IconNavLink>
            </li>
          </ul>
        </nav>

        <main className={style['Main']}>
          <div className={style['Main-status']}>
            <GlobalStatus id="error" no_animation />
          </div>
          <div className={style['Main-content']}>
            {globalError ? (
              <GlobalError statusCode={globalError === 'unknown' ? undefined : String(globalError)} />
            ) : (
              <Outlet />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default App;
